import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useObjectContext } from '@/hooks/useObjectContext';
import {
	dealsApi,
	useUpdateClientDealRowsMutation,
	useUpdateDealRowMutation,
} from '@/features/deals/dealsApi';
import { clientsApi, useUpdateClientxMutation } from '@/features/clients/clientsApi';
import { replaceFolderName } from '@/utils/constants';
import { triggerRefresh } from '@/features/table/attachmentsTableSlice';
import { useRenameSharepointItemMutation } from '@/features/msGraph/msGraphApi';
import { useTabContext } from '@/components/layouts/TabHeader/TabContext';
import { showToast } from '@/features/toast/toastSlice';
import { useCreateActivityMutation } from '@/features/engagements/engagementsApi';
import { useUpdateRelationshipCompanyMutation } from '@/features/relationshipCompanies/relationshipCompaniesApi';

/**
 * Saves the edited header: renames the deal or client (Folio, Insight and the SharePoint
 * folder, reverting a partial client rename on failure), then saves the overview.
 */
export const useNameSave = ({ setError }) => {
	const {
		clientRow,
		dealRow,
		dealId,
		clientId,
		isClientView: isClient,
		relationshipCompany,
		isRelationshipCompanyView,
	} = useObjectContext();
	const { fields, setFields, titleChanged, saveOverview, isSavingOverview } = useTabContext();

	const dispatch = useDispatch();

	const [isLoadingUpdate, setIsLoadingUpdate] = useState(false);

	const [updateClientDealRows, { isLoading: isUpdatingClientDeals }] =
		useUpdateClientDealRowsMutation();
	const [updateDealRow, { isLoading: isUpdatingDealRow }] = useUpdateDealRowMutation();
	const [createActivity] = useCreateActivityMutation();
	const [updateClient] = useUpdateClientxMutation();
	const [renameSharepointItem] = useRenameSharepointItemMutation();
	const [updateRelationshipCompany] = useUpdateRelationshipCompanyMutation();

	// Used to invalidate the sharepoint folders
	const invalidateTags = () => {
		dispatch(triggerRefresh());
		setTimeout(() => {
			if (dealId) {
				dispatch(dealsApi.util.invalidateTags([{ type: 'DEAL_ROW', id: dealId }]));
			}
			if (clientRow) {
				dispatch(
					clientsApi.util.invalidateTags([
						{
							type: 'CLIENT_ROW',
							id: clientRow.clientId ?? clientId,
						},
					]),
				);
			}
		}, 5000);
	};

	const updateDealName = async (trimmedName) => {
		try {
			const newFolderName = replaceFolderName(trimmedName);
			const res = await renameSharepointItem({
				driveId: dealRow?.clientFolderDriveId,
				itemId: dealRow?.driveId,
				name: newFolderName,
			})
				.unwrap()
				.catch((e) => {
					console.log('Error: name already exists', e?.message);
					setError(e?.message ?? 'Error updating folder name');
					throw e;
				});

			const webUrl = decodeURI((res?.webUrl ?? '').normalize());
			const urls = webUrl.split('/');
			const dealFolderPath = urls.length > 4 ? urls.slice(urls.length - 4).join('/') : null;
			const properties = {
				dealName: trimmedName,
				...(res?.webUrl && { dealFolderUrl: webUrl }),
				...(dealFolderPath && { dealFolderPath }),
			};
			await updateDealRow({ dealId: dealRow?.dealId, properties }).unwrap();

			invalidateTags();
		} catch (error) {
			console.error('Failed to update:', error);
			setFields('title', dealRow.dealName);
		}
	};

	const updateRelationshipCompanyName = async (trimmedName) => {
		setIsLoadingUpdate(true);
		const previousName = relationshipCompany?.name ?? '';
		let companyUpdated = false;
		try {
			await updateRelationshipCompany({
				relationshipCompanyId: relationshipCompany?.id,
				properties: { name: trimmedName },
			}).unwrap();
			companyUpdated = true;

			if (relationshipCompany?.driveId && relationshipCompany?.clientFolderDriveId) {
				await renameSharepointItem({
					driveId: relationshipCompany.clientFolderDriveId,
					itemId: relationshipCompany.driveId,
					name: replaceFolderName(trimmedName),
				}).unwrap();
			}
			setError('');
			invalidateTags();
		} catch (error) {
			console.error('Failed to update relationship company name:', error);
			if (companyUpdated) {
				try {
					await updateRelationshipCompany({
						relationshipCompanyId: relationshipCompany?.id,
						properties: { name: previousName },
					}).unwrap();
				} catch (revertError) {
					console.error('Failed to revert relationship company name:', revertError);
					const message = 'The company name and folder are out of sync.';
					setError(message);
					setFields('title', previousName);
					dispatch(
						showToast({
							message,
							error: true,
							autohide: true,
						}),
					);
					return;
				}
			}
			const message =
				error?.data?.message ?? error?.message ?? 'Failed to update company name.';
			setError(message);
			setFields('title', previousName);
			dispatch(
				showToast({
					message,
					error: true,
					autohide: true,
				}),
			);
		} finally {
			setIsLoadingUpdate(false);
		}
	};

	const updateClientName = async (trimmedName) => {
		setIsLoadingUpdate(true);
		const originalName = clientRow?.name;
		const completedCalls = [];

		try {
			await updateClient({
				clientId,
				insight: { Name: trimmedName },
				firebase: {},
			}).unwrap();
			completedCalls.push('clientName');

			await renameSharepointItem({
				driveId: clientRow?.clientFolderDriveId,
				itemId: clientRow?.driveId,
				name: replaceFolderName(trimmedName),
			}).unwrap();
			completedCalls.push('sharepoint');

			await updateClientDealRows({
				clientId,
				newName: trimmedName,
			}).unwrap();
			completedCalls.push('clientDeals');

			await createActivity({
				// Activities reference the client by its Firestore doc id.
				clientId: clientRow.id,
				properties: {
					originalName: originalName,
					newName: trimmedName,
				},
				activityType: 'clientNameChange',
			}).unwrap();

			invalidateTags();
			setError(false);
			setIsLoadingUpdate(false);
			return true;
		} catch (error) {
			console.error('Error during client name update:', error);
			dispatch(
				showToast({
					message: 'Failed to update client name.',
					error: true,
					autohide: true,
				}),
			);

			try {
				if (completedCalls.includes('clientName')) {
					await updateClient({
						clientId,
						insight: { Name: originalName },
						firebase: {},
					}).catch((e) => console.error('error reverting client name:', e));
				}

				if (completedCalls.includes('sharepoint')) {
					await renameSharepointItem({
						driveId: clientRow?.clientFolderDriveId,
						itemId: clientRow?.driveId,
						name: replaceFolderName(originalName),
					});
				}

				if (completedCalls.includes('clientDeals')) {
					await updateClientDealRows({
						clientId,
						newName: originalName,
					}).catch((e) => console.error('error reverting:', e));
				}
			} catch (error) {
				console.error('Error reverting client:', error);
			}
			setIsLoadingUpdate(false);
			setError('Failed to update client name. All changes reverted.');
			setFields('title', originalName);
			return false;
		}
	};

	const save = async () => {
		const trimmedName = fields.title.trim();

		if (titleChanged) {
			if (isRelationshipCompanyView) {
				await updateRelationshipCompanyName(trimmedName);
			} else if (dealRow) {
				await updateDealName(trimmedName);
			} else if (isClient) {
				await updateClientName(trimmedName);
			}
		}
		await saveOverview();
	};

	return {
		save,
		isSaving: isUpdatingDealRow || isSavingOverview || isUpdatingClientDeals || isLoadingUpdate,
	};
};
